import React, {useEffect, useMemo, useState} from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Container,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {Trash2, Plus, Save, ArrowUp, ArrowDown, Undo2} from "lucide-react";
import {useNavigate, useParams} from "react-router-dom";
import {useConfirmDialog} from "../../../hooks/use-confirm-dialog.ts";
import {useQueryClient} from "@tanstack/react-query";
import {
  useGetApprovalFlowByIdQuery,
  useUpdateApprovalFlowMutation,
  useCreateApprovalFlowMutation,
  ApprovalStepInput,
} from "../../../graphql/graphql.ts";
import {LoadingPage} from "../../LoadingPage.tsx";
import {useUserData} from "../../../hooks/use-user-data-provider.ts";
import {Permission} from "../../../utils/permissions.ts";
import {NotFoundPage} from "../../NotFoundPage.tsx";
import toast from "react-hot-toast";
import {User, UserAutocomplete} from "../../../components/input/UserAutocomplete.tsx";

interface ExtendedApprovalStepInput extends ApprovalStepInput {
  approver: User | null;
  isDeleted?: boolean;
}

export const ApprovalFlowEditPage: React.FC = () => {
  const {id} = useParams();
  const navigate = useNavigate();
  const {data, isLoading} = useGetApprovalFlowByIdQuery(
    {id: id!},
    {enabled: id !== "new"}
  );
  const {mutateAsync: updateApprovalFlow} = useUpdateApprovalFlowMutation();
  const {mutateAsync: createApprovalFlow} = useCreateApprovalFlowMutation();
  const queryClient = useQueryClient();
  const queryKey = useGetApprovalFlowByIdQuery.getKey({id: id!});
  const {permissions} = useUserData();
  const confirmDialog = useConfirmDialog();
  
  const isNew = id === "new";
  const flow = data?.getApprovalFlowById;
  
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [steps, setSteps] = useState<ExtendedApprovalStepInput[]>([]);
  
  // データ取得後にstateを更新
  useEffect(() => {
    if (flow) {
      setName(flow.name);
      setDescription(flow.description || "");
      setSteps(
        flow.steps.map((step) => ({
          id: step.id,
          name: step.name,
          description: step.description,
          stepOrder: step.stepOrder,
          approverId: step.approverId,
          approver: {
            id: step.approver.id,
            name: step.approver.name,
            email: step.approver.email,
            organizationId: step.approver.organizationId,
          },
          isDeleted: step.isDeleted,
        }))
      );
    }
  }, [flow]);
  
  // 保存ボタンの有効/無効を制御
  const isSaveDisabled = useMemo(() => {
    // 名前が空の場合
    if (!name.trim()) return true;
    
    // 有効なステップが1つもない場合
    const activeSteps = steps.filter(step => !step.isDeleted);
    if (activeSteps.length === 0) return true;
    
    // 各ステップの必須項目をチェック
    return activeSteps.some(step => {
      // ステップ名が空の場合
      if (!step.name.trim()) return true;
      // 承認者が未選択の場合
      if (!step.approver) return true;
      return false;
    });
  }, [name, steps]);
  
  const handleSave = async () => {
    try {
      const input = {
        name,
        description,
        steps: steps.map(step => ({
          id: step.id,
          name: step.name,
          description: step.description,
          stepOrder: step.stepOrder,
          approverId: step.approver?.id || "",
          isDeleted: step.isDeleted,
        })),
      };
      if (isNew) {
        const result = await createApprovalFlow({
          input,
        });
        
        if (result?.createApprovalFlow) {
          toast.success("承認フローを作成しました");
          navigate("/admin/approvalFlow");
        }
      } else {
        const result = await updateApprovalFlow({
          input: {
            id: id!,
            ...input,
          },
        });
        
        if (result?.updateApprovalFlow) {
          // キャッシュを更新
          queryClient.setQueryData(
            queryKey,
            {
              getApprovalFlowById: result.updateApprovalFlow,
            },
          );
          toast.success("承認フローを更新しました");
          navigate("/admin/approvalFlow");
        }
      }
    } catch (error) {
      console.error("Failed to save approval flow:", error);
      toast.error(
        `承認フローの${isNew ? "作成" : "更新"}に失敗しました`,
      );
    }
  };
  
  const handleDeleteStep = (index: number) => {
    confirmDialog.open({
      fullWidth: true,
      icon: <Trash2 color="error"/>,
      title: (
        <Typography
          sx={{color: "primary.contrastText"}}
          fontWeight={"bold"}
        >
          承認ステップの削除
        </Typography>
      ),
      children: (
        <Stack direction={"column"} spacing={2} minWidth={"250px"}>
          <Typography>
            {`${steps[index].name}を削除してもよろしいですか？`}
          </Typography>
          <Stack direction={"row"} spacing={2}>
            <Button
              fullWidth
              variant={"outlined"}
              onClick={async () => confirmDialog.close()}
            >
              キャンセル
            </Button>
            <Button
              fullWidth
              variant={"contained"}
              color="error"
              onClick={async () => {
                setSteps((prev) => {
                  const newSteps = [...prev];
                  if (newSteps[index].id) {
                    // 論理削除に変更
                    newSteps[index] = {
                      ...newSteps[index],
                      isDeleted: true,
                    };
                    return newSteps;
                  } else {
                    // 削除
                    return newSteps.filter((_, i) => i !== index);
                  }
                });
                confirmDialog.close();
              }}
            >
              削除する
            </Button>
          </Stack>
        </Stack>
      ),
    });
  };
  
  const handleRestoreStep = (index: number) => {
    confirmDialog.open({
      fullWidth: true,
      icon: <Undo2 color="primary"/>,
      title: (
        <Typography
          sx={{color: "primary.contrastText"}}
          fontWeight={"bold"}
        >
          承認ステップの復元
        </Typography>
      ),
      children: (
        <Stack direction={"column"} spacing={2} minWidth={"250px"}>
          <Typography>
            {`${steps[index].name}を復元してもよろしいですか？`}
          </Typography>
          <Stack direction={"row"} spacing={2}>
            <Button
              fullWidth
              variant={"outlined"}
              onClick={async () => confirmDialog.close()}
            >
              キャンセル
            </Button>
            <Button
              fullWidth
              variant={"contained"}
              onClick={async () => {
                setSteps((prev) => {
                  const newSteps = [...prev];
                  // 論理削除を解除
                  newSteps[index] = {
                    ...newSteps[index],
                    isDeleted: false,
                  };
                  return newSteps;
                });
                confirmDialog.close();
              }}
            >
              復元する
            </Button>
          </Stack>
        </Stack>
      ),
    });
  };
  
  const handleAddStep = () => {
    setSteps((prev) => [
      ...prev,
      {
        name: "",
        description: "",
        stepOrder: prev.filter(step => !step.isDeleted).length,
        approverId: "",
        approver: null,
        isDeleted: false,
      },
    ]);
  };
  
  const handleMoveStep = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= steps.length) return;
    
    setSteps((prev) => {
      const newSteps = [...prev];
      const [movedStep] = newSteps.splice(fromIndex, 1);
      newSteps.splice(toIndex, 0, movedStep);
      // stepOrderを更新（削除されていないステップのみ）
      const activeSteps = newSteps.filter(step => !step.isDeleted);
      return newSteps.map(step => ({
        ...step,
        stepOrder: step.isDeleted ? step.stepOrder : activeSteps.indexOf(step),
      }));
    });
  };
  
  if (isLoading) {
    return <LoadingPage/>;
  }
  
  if (!isNew && !flow) {
    return <NotFoundPage/>;
  }
  // 権限チェック
  if (!permissions.includes(Permission.WriteApprove)) {
    return <NotFoundPage/>;
  }
  
  return (
    <Container maxWidth="lg" sx={{py: 4}}>
      <Box sx={{maxWidth: 1400, margin: '0 auto'}}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 'bold',
              background: 'linear-gradient(45deg, primary.main, primary.dark)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {isNew ? "承認フローの作成" : "承認フローの編集"}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            startIcon={<Save size={20}/>}
            disabled={isSaveDisabled}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              boxShadow: 2,
              '&:hover': {
                boxShadow: 4,
              },
              '&.Mui-disabled': {
                backgroundColor: 'action.disabledBackground',
                color: 'action.disabled',
              },
            }}
          >
            保存
          </Button>
        </Box>
        
        <Card sx={{
          mb: 4,
          boxShadow: 3,
          borderRadius: 2,
        }}>
          <CardHeader title="基本情報" sx={{backgroundColor: 'primary.main', color: 'white'}}/>
          <CardContent>
            <Stack spacing={3}>
              <TextField
                label="名前"
                value={name}
                size="small"
                onChange={(e) => setName(e.target.value)}
                fullWidth
                required
                error={!name.trim()}
                helperText={!name.trim() ? "名前を入力してください" : ""}
              />
              <TextField
                label="説明"
                size="small"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
                multiline
                rows={3}
              />
            </Stack>
          </CardContent>
        </Card>
        
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography
            variant="h5"
            component="h2"
            sx={{
              fontWeight: 'bold',
              color: 'primary.main',
            }}
          >
            承認ステップ
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddStep}
            startIcon={<Plus size={20}/>}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              boxShadow: 2,
              '&:hover': {
                boxShadow: 4,
              },
            }}
          >
            ステップを追加
          </Button>
        </Box>
        
        <TableContainer
          component={Paper}
          sx={{
            boxShadow: 3,
            borderRadius: 2,
            overflow: 'hidden',
          }}>
          <Table>
            <TableHead>
              <TableRow sx={{backgroundColor: 'primary.main'}}>
                <TableCell sx={{color: 'white', fontWeight: 'bold'}}>順序</TableCell>
                <TableCell sx={{color: 'white', fontWeight: 'bold'}}>名前</TableCell>
                <TableCell sx={{color: 'white', fontWeight: 'bold'}}>説明</TableCell>
                <TableCell sx={{color: 'white', fontWeight: 'bold'}}>承認者</TableCell>
                <TableCell sx={{color: 'white', fontWeight: 'bold'}} align="right">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {steps.map((step, index) => (
                <TableRow
                  key={index}
                  sx={{
                    bgcolor: step.isDeleted ? "rgba(0, 0, 0, 0.05)" : "inherit",
                    '&:hover': {
                      bgcolor: step.isDeleted ? "rgba(0, 0, 0, 0.08)" : "rgba(0, 0, 0, 0.03)",
                    },
                    transition: 'background-color 0.2s ease',
                  }}
                >
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={step.name}
                      onChange={(e) =>
                        setSteps((prev) => {
                          const newSteps = [...prev];
                          newSteps[index] = {
                            ...newSteps[index],
                            name: e.target.value,
                          };
                          return newSteps;
                        })
                      }
                      fullWidth
                      required
                      error={!step.name.trim()}
                      helperText={!step.name.trim() ? "ステップ名を入力してください" : ""}
                      disabled={step.isDeleted}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      value={step.description || ""}
                      size="small"
                      onChange={(e) =>
                        setSteps((prev) => {
                          const newSteps = [...prev];
                          newSteps[index] = {
                            ...newSteps[index],
                            description: e.target.value,
                          };
                          return newSteps;
                        })
                      }
                      fullWidth
                      disabled={step.isDeleted}
                    />
                  </TableCell>
                  <TableCell>
                    <UserAutocomplete
                      value={step.approver}
                      onChange={(user) =>
                        setSteps((prev) => {
                          const newSteps = [...prev];
                          newSteps[index] = {
                            ...newSteps[index],
                            approver: user,
                            approverId: user?.id || "",
                          };
                          return newSteps;
                        })
                      }
                      disabled={step.isDeleted}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      {step.isDeleted ? (
                        <IconButton
                          onClick={() => handleRestoreStep(index)}
                          size="small"
                          color="primary"
                          sx={{
                            '&:hover': {
                              backgroundColor: 'primary.light',
                              color: 'white',
                            },
                            transition: 'all 0.2s ease',
                          }}
                        >
                          <Undo2 size={20}/>
                        </IconButton>
                      ) : (
                        <>
                          <IconButton
                            onClick={() => handleMoveStep(index, index - 1)}
                            disabled={index === 0}
                            size="small"
                            sx={{
                              '&:hover': {
                                backgroundColor: 'primary.light',
                                color: 'white',
                              },
                              transition: 'all 0.2s ease',
                              '&.Mui-disabled': {
                                opacity: 0.5,
                              }
                            }}
                          >
                            <ArrowUp size={20}/>
                          </IconButton>
                          <IconButton
                            onClick={() => handleMoveStep(index, index + 1)}
                            disabled={index === steps.length - 1}
                            size="small"
                            sx={{
                              '&:hover': {
                                backgroundColor: 'primary.light',
                                color: 'white',
                              },
                              transition: 'all 0.2s ease',
                              '&.Mui-disabled': {
                                opacity: 0.5,
                              }
                            }}
                          >
                            <ArrowDown size={20}/>
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={() => handleDeleteStep(index)}
                            size="small"
                            sx={{
                              '&:hover': {
                                backgroundColor: 'error.light',
                                color: 'white',
                              },
                              transition: 'all 0.2s ease',
                            }}
                          >
                            <Trash2 size={20}/>
                          </IconButton>
                        </>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {steps.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{py: 4}}>
                    <Typography
                      color="textSecondary"
                      sx={{
                        fontStyle: 'italic',
                        opacity: 0.8
                      }}
                    >
                      承認ステップが存在しません
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Typography mt={5}>
          全ての承認者は、承認権限を持つユーザーである必要があります。
        </Typography>
      </Box>
    </Container>
  );
};

export default ApprovalFlowEditPage;
